import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, LineChart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import PortfolioStructureSection from './PortfolioStructureSection';
import TaxesAndFeesSection from './TaxesAndFeesSection';
import IISSection from './IISSection';
import InflationSection from './InflationSection';
import CashFlowSection from './CashFlowSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета инвестиций
 */
const CalculationForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  setIsCalculating,
  results,
  setResults,
  portfolioHistory,
  setPortfolioHistory,
  setAssetAllocationChart,
  portfolioTypes,
  assetTypes,
  savePortfolio
}) => {
  // Состояние для имени сохраняемого портфеля
  const [savePortfolioName, setSavePortfolioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev };
      
      // Обработка вложенных свойств
      if (name.includes('.')) {
        const [parentProp, childProp] = name.split('.');
        newState[parentProp] = {
          ...prev[parentProp],
          [childProp]: fieldValue
        };
      } else {
        newState[name] = fieldValue;
      }
      
      // Обновляем структуру портфеля при изменении типа портфеля
      if (name === 'portfolioType' && fieldValue !== 'custom') {
        const selectedPortfolioType = portfolioTypes.find(type => type.id === fieldValue);
        if (selectedPortfolioType) {
          newState.assetAllocation = { ...selectedPortfolioType.allocation };
        }
      }
      
      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeTaxes' && !checked) {
        newState.taxRate = '13';
      }
      
      if (name === 'includeInflation' && !checked) {
        newState.inflationRate = '7';
      }
      
      if (name === 'includeFees' && !checked) {
        newState.brokerFee = '0.3';
        newState.managementFee = '1';
      }
      
      if (name === 'useIIS' && !checked) {
        newState.iisType = 'taxDeduction';
        newState.iisDeductionLimit = '400000';
      }
      
      if (name === 'includeCashFlow' && !checked) {
        newState.additionalInvestments = [];
        newState.withdrawals = [];
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [portfolioTypes, errors, setErrors, setFormData]);

  // Обработчик изменения структуры портфеля
  const handleAssetAllocationChange = useCallback((asset, value) => {
    setFormData(prev => {
      const newAssetAllocation = {
        ...prev.assetAllocation,
        [asset]: value
      };
      
      // Если тип портфеля был предопределенным, меняем его на пользовательский
      const newState = { 
        ...prev,
        assetAllocation: newAssetAllocation
      };
      
      if (prev.portfolioType !== 'custom') {
        newState.portfolioType = 'custom';
      }
      
      return newState;
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      initialInvestment, monthlyContribution, investmentTerm, startDate,
      expectedReturns, inflationRate, taxRate, brokerFee, managementFee,
      iisDeductionLimit, assetAllocation, portfolioType
    } = formData;

    // Парсинг числовых значений
    const numInitialInvestment = parseFloatOrDefault(initialInvestment, -1);
    const numMonthlyContribution = parseFloatOrDefault(monthlyContribution, 0);
    const numInvestmentTermYears = parseFloatOrDefault(investmentTerm, -1);
    const numInflationRate = parseFloatOrDefault(inflationRate, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const numBrokerFee = parseFloatOrDefault(brokerFee, -1);
    const numManagementFee = parseFloatOrDefault(managementFee, -1);
    const numIISDeductionLimit = parseFloatOrDefault(iisDeductionLimit, -1);

    // Валидация базовых полей
    if (numInitialInvestment <= 0) newErrors.initialInvestment = 'Сумма должна быть положительной.';
    if (!newErrors.initialInvestment && numInitialInvestment > 100000000) 
      newErrors.initialInvestment = 'Максимальная сумма 100 млн ₽.';
    
    if (numMonthlyContribution < 0) newErrors.monthlyContribution = 'Сумма не может быть отрицательной.';
    if (!newErrors.monthlyContribution && numMonthlyContribution > 1000000) 
      newErrors.monthlyContribution = 'Максимальная сумма ежемесячного пополнения 1 млн ₽.';
    
    if (numInvestmentTermYears <= 0) newErrors.investmentTerm = 'Срок должен быть положительным.';
    if (!newErrors.investmentTerm && numInvestmentTermYears > 50) 
      newErrors.investmentTerm = 'Максимальный срок 50 лет.';

    // Проверка даты начала инвестирования
    if (!startDate) newErrors.startDate = 'Укажите дату начала инвестирования.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация инфляции
    if (formData.includeInflation) {
      if (numInflationRate < 0) newErrors.inflationRate = 'Инфляция не может быть отрицательной.';
      if (!newErrors.inflationRate && numInflationRate > 100) 
        newErrors.inflationRate = 'Инфляция не может превышать 100%.';
    }

    // Валидация налогов
    if (formData.includeTaxes) {
      if (numTaxRate < 0) newErrors.taxRate = 'Ставка налога не может быть отрицательной.';
      if (!newErrors.taxRate && numTaxRate > 100) 
        newErrors.taxRate = 'Ставка налога не может превышать 100%.';
    }

    // Валидация комиссий
    if (formData.includeFees) {
      if (numBrokerFee < 0) newErrors.brokerFee = 'Комиссия не может быть отрицательной.';
      if (!newErrors.brokerFee && numBrokerFee > 100) 
        newErrors.brokerFee = 'Комиссия не может превышать 100%.';
      
      if (numManagementFee < 0) newErrors.managementFee = 'Комиссия не может быть отрицательной.';
      if (!newErrors.managementFee && numManagementFee > 100) 
        newErrors.managementFee = 'Комиссия не может превышать 100%.';
    }

    // Валидация ИИС
    if (formData.useIIS) {
      if (numIISDeductionLimit < 0) newErrors.iisDeductionLimit = 'Лимит не может быть отрицательным.';
      if (!newErrors.iisDeductionLimit && numIISDeductionLimit > 1000000) 
        newErrors.iisDeductionLimit = 'Максимальный лимит 1 млн ₽.';
    }

    // Валидация структуры портфеля
    if (portfolioType === 'custom') {
      const totalAllocation = Object.values(assetAllocation).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
      
      if (Math.abs(totalAllocation - 100) > 0.01) {
        newErrors.assetAllocation = `Распределение активов должно равняться 100% (сейчас ${Math.round(totalAllocation)}%).`;
      }
    }

    // Валидация ожидаемой доходности
    for (const assetType of assetTypes) {
      const assetReturn = parseFloatOrDefault(expectedReturns[assetType.id], -1);
      if (assetReturn < 0) {
        newErrors[`expectedReturns.${assetType.id}`] = 'Доходность не может быть отрицательной.';
      }
      if (!newErrors[`expectedReturns.${assetType.id}`] && assetReturn > 100) {
        newErrors[`expectedReturns.${assetType.id}`] = 'Доходность не может превышать 100%.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, assetTypes, setErrors]);

  // Функция расчета доходности инвестиций
  const calculateInvestmentReturns = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setPortfolioHistory([]);
    setAssetAllocationChart([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const initialInvestment = parseFloatOrDefault(formData.initialInvestment);
        const monthlyContribution = parseFloatOrDefault(formData.monthlyContribution);
        const investmentTermYears = parseFloatOrDefault(formData.investmentTerm);
        const investmentTermMonths = Math.round(investmentTermYears * 12);
        const startDateObj = new Date(formData.startDate);
        
        // Настройки
        const includeInflation = formData.includeInflation;
        const inflationRate = parseFloatOrDefault(formData.inflationRate) / 100;
        const includeTaxes = formData.includeTaxes;
        const taxRate = parseFloatOrDefault(formData.taxRate) / 100;
        const includeFees = formData.includeFees;
        const brokerFee = parseFloatOrDefault(formData.brokerFee) / 100;
        const managementFee = parseFloatOrDefault(formData.managementFee) / 100;
        const useIIS = formData.useIIS;
        const iisType = formData.iisType;
        const iisDeductionLimit = parseFloatOrDefault(formData.iisDeductionLimit);
        const includeCashFlow = formData.includeCashFlow;
        const additionalInvestments = formData.additionalInvestments || [];
        const withdrawals = formData.withdrawals || [];
        
        // Структура портфеля и доходность
        const assetAllocation = formData.assetAllocation;
        const expectedReturns = {};
        for (const assetType of assetTypes) {
          expectedReturns[assetType.id] = parseFloatOrDefault(formData.expectedReturns[assetType.id]) / 100;
        }

        // --- Расчет портфолио ---
        // Инициализация начального состояния
        let currentPortfolioValue = initialInvestment;
        let totalInvested = initialInvestment;
        let totalWithdrawals = 0;
        let totalTaxesPaid = 0;
        let totalFeesPaid = 0;
        let totalInflationImpact = 0;
        let totalTaxDeductions = 0;
        
        // Распределение активов
        const assetValues = {};
        for (const assetType of assetTypes) {
          assetValues[assetType.id] = currentPortfolioValue * (assetAllocation[assetType.id] / 100);
        }
        
        // История портфеля (помесячная)
        const history = [];
        const assetHistory = {};
        
        // Инициализация истории активов
        assetTypes.forEach(type => {
          assetHistory[type.id] = {
            id: type.id, 
            name: type.name,
            color: type.color,
            data: []
          };
        });
        
        // Параметры ИИС
        const iisOpenDate = new Date(startDateObj);
        let iisDeductionsUsed = 0;
        const maxIISYears = 3; // Срок действия ИИС
        
        // --- Расчет по месяцам ---
        for (let month = 1; month <= investmentTermMonths; month++) {
          const currentDate = new Date(startDateObj);
          currentDate.setMonth(startDateObj.getMonth() + month - 1);
          
          // Начинаем с предыдущего баланса
          let monthStartBalance = currentPortfolioValue;
          
          // 1. Добавляем ежемесячный взнос
          if (monthlyContribution > 0) {
            currentPortfolioValue += monthlyContribution;
            totalInvested += monthlyContribution;
            
            // Распределяем по активам согласно аллокации
            for (const assetType of assetTypes) {
              assetValues[assetType.id] += monthlyContribution * (assetAllocation[assetType.id] / 100);
            }
          }
          
          // 2. Обрабатываем дополнительные инвестиции и снятия
          if (includeCashFlow) {
            // Дополнительные инвестиции
            const currentMonthInvestments = additionalInvestments
              .filter(inv => inv.month === month)
              .reduce((sum, inv) => sum + parseFloatOrDefault(inv.amount, 0), 0);
            
            if (currentMonthInvestments > 0) {
              currentPortfolioValue += currentMonthInvestments;
              totalInvested += currentMonthInvestments;
              
              // Распределяем по активам
              for (const assetType of assetTypes) {
                assetValues[assetType.id] += currentMonthInvestments * (assetAllocation[assetType.id] / 100);
              }
            }
            
            // Снятия средств
            const currentMonthWithdrawals = withdrawals
              .filter(w => w.month === month)
              .reduce((sum, w) => sum + parseFloatOrDefault(w.amount, 0), 0);
            
            if (currentMonthWithdrawals > 0 && currentMonthWithdrawals <= currentPortfolioValue) {
              totalWithdrawals += currentMonthWithdrawals;
              
              // Рассчитываем налог на прибыль при выводе средств
              if (includeTaxes && !useIIS) {
                const withdrawalRatio = currentMonthWithdrawals / currentPortfolioValue;
                const costBasis = totalInvested * withdrawalRatio;
                const taxableGain = Math.max(0, currentMonthWithdrawals - costBasis);
                const taxPayment = taxableGain * taxRate;
                
                totalTaxesPaid += taxPayment;
                currentPortfolioValue -= taxPayment; // Уменьшаем стоимость портфеля на сумму налога
              }
              
              currentPortfolioValue -= currentMonthWithdrawals;
              
              // Распределяем снятие по активам
              for (const assetType of assetTypes) {
                const withdrawalFromAsset = assetValues[assetType.id] * (currentMonthWithdrawals / (currentPortfolioValue + currentMonthWithdrawals));
                assetValues[assetType.id] -= withdrawalFromAsset;
              }
            }
          }
          
          // 3. Расчет ежемесячной доходности для каждого актива
          for (const assetType of assetTypes) {
            const monthlyReturn = expectedReturns[assetType.id] / 12;
            const assetReturn = assetValues[assetType.id] * monthlyReturn;
            assetValues[assetType.id] += assetReturn;
          }
          
          // 4. Пересчитываем общую стоимость портфеля
          currentPortfolioValue = Object.values(assetValues).reduce((sum, value) => sum + value, 0);
          
          // 5. Вычитаем комиссии (если включено)
          let monthlyFees = 0;
          if (includeFees) {
            // Комиссия управления (от стоимости активов)
            const managementFeeAmount = currentPortfolioValue * (managementFee / 12);
            monthlyFees += managementFeeAmount;
            
            // Брокерская комиссия (от оборота - пополнения/снятия)
            const transactions = monthlyContribution + 
              (includeCashFlow ? additionalInvestments
                .filter(inv => inv.month === month)
                .reduce((sum, inv) => sum + parseFloatOrDefault(inv.amount, 0), 0) : 0) +
              (includeCashFlow ? withdrawals
                .filter(w => w.month === month)
                .reduce((sum, w) => sum + parseFloatOrDefault(w.amount, 0), 0) : 0);
            
            const brokerFeeAmount = transactions * brokerFee;
            monthlyFees += brokerFeeAmount;
            
            // Вычитаем комиссии из стоимости портфеля
            currentPortfolioValue -= monthlyFees;
            totalFeesPaid += monthlyFees;
            
            // Распределяем комиссии пропорционально по активам
            for (const assetType of assetTypes) {
              const assetFee = monthlyFees * (assetValues[assetType.id] / (currentPortfolioValue + monthlyFees));
              assetValues[assetType.id] -= assetFee;
            }
          }
          
          // 6. Расчет влияния инфляции (для статистики)
          let monthlyInflation = 0;
          if (includeInflation) {
            monthlyInflation = monthStartBalance * (inflationRate / 12);
            totalInflationImpact += monthlyInflation;
          }
          
          // 7. Обработка налогового вычета по ИИС (раз в год)
          let monthlyTaxDeduction = 0;
          if (useIIS) {
            const monthsSinceStart = month - 1;
            const yearsSinceStart = Math.floor(monthsSinceStart / 12);
            
            // Проверяем, что это последний месяц года и ИИС еще действует
            if (monthsSinceStart % 12 === 0 && yearsSinceStart < maxIISYears) {
              // Рассчитываем сумму инвестиций за год
              const yearStart = Math.max(0, monthsSinceStart - 12);
              const yearEnd = monthsSinceStart;
              
              let yearInvestments = 0;
              // Учитываем начальную инвестицию
              if (yearStart === 0) yearInvestments += initialInvestment;
              
              // Учитываем ежемесячные взносы
              for (let m = yearStart + 1; m <= yearEnd; m++) {
                yearInvestments += monthlyContribution;
                
                // Учитываем дополнительные инвестиции
                if (includeCashFlow) {
                  yearInvestments += additionalInvestments
                    .filter(inv => inv.month === m)
                    .reduce((sum, inv) => sum + parseFloatOrDefault(inv.amount, 0), 0);
                }
              }
              
              // Ограничиваем по лимиту вычета
              const deductionBase = Math.min(yearInvestments, iisDeductionLimit);
              
              if (iisType === 'taxDeduction') {
                // Тип А: возврат НДФЛ 13% на взносы до 400т.р.
                monthlyTaxDeduction = deductionBase * taxRate;
                totalTaxDeductions += monthlyTaxDeduction;
                
                // Добавляем вычет к текущей стоимости портфеля
                currentPortfolioValue += monthlyTaxDeduction;
                
                // Распределяем вычет по активам
                for (const assetType of assetTypes) {
                  assetValues[assetType.id] += monthlyTaxDeduction * (assetAllocation[assetType.id] / 100);
                }
              }
            }
            
            // Если прошло 3 года, проверяем налог по типу Б
            if (iisType === 'taxFree' && month >= maxIISYears * 12) {
              // Тип Б: освобождение от налога на прибыль при закрытии ИИС
              // Не нужно ничего делать на этом этапе, но при выводе средств налог не взимается
            }
          }
          
          // 8. Сохраняем данные о состоянии портфеля в этот месяц
          history.push({
            month,
            date: currentDate,
            startBalance: monthStartBalance,
            contributions: monthlyContribution,
            additionalInvestments: includeCashFlow ? additionalInvestments
              .filter(inv => inv.month === month)
              .reduce((sum, inv) => sum + parseFloatOrDefault(inv.amount, 0), 0) : 0,
            withdrawals: includeCashFlow ? withdrawals
              .filter(w => w.month === month)
              .reduce((sum, w) => sum + parseFloatOrDefault(w.amount, 0), 0) : 0,
            fees: monthlyFees,
            taxDeduction: monthlyTaxDeduction,
            inflation: monthlyInflation,
            endBalance: currentPortfolioValue,
            assetValues: { ...assetValues }
          });
          
          // Обновляем историю по каждому активу
          assetTypes.forEach(type => {
            assetHistory[type.id].data.push({
              month,
              value: assetValues[type.id]
            });
          });
        }
        
        // --- Подготовка данных для диаграмм ---
        const assetAllocationData = assetTypes
          .filter(type => assetAllocation[type.id] > 0)
          .map(type => ({
            id: type.id,
            name: type.name,
            value: assetValues[type.id],
            color: type.color,
            percentage: (assetValues[type.id] / currentPortfolioValue) * 100
          }));
        
        // Рассчитываем годовую доходность (IRR)
        const totalInvestmentPeriod = investmentTermYears;
        const totalReturn = (currentPortfolioValue - (totalInvested - totalWithdrawals)) / (totalInvested - totalWithdrawals);
        const annualizedReturn = Math.pow(1 + totalReturn, 1 / totalInvestmentPeriod) - 1;
        
        // Рассчитываем реальную доходность с учетом инфляции
        const inflationFactor = includeInflation ? Math.pow(1 + inflationRate, totalInvestmentPeriod) : 1;
        const realAnnualizedReturn = (1 + annualizedReturn) / inflationFactor - 1;
        
        // --- Готовим итоговые результаты ---
        const finalResults = {
          initialInvestment,
          totalInvested,
          finalPortfolioValue: currentPortfolioValue,
          totalContributions: totalInvested - initialInvestment,
          totalWithdrawals,
          totalTaxesPaid,
          totalFeesPaid,
          totalInflationImpact,
          totalTaxDeductions,
          
          absoluteReturn: currentPortfolioValue - (totalInvested - totalWithdrawals),
          percentageReturn: ((currentPortfolioValue / (totalInvested - totalWithdrawals)) - 1) * 100,
          annualizedReturn: annualizedReturn * 100,
          realAnnualizedReturn: realAnnualizedReturn * 100,
          
          inflationAdjustedValue: includeInflation ? currentPortfolioValue / inflationFactor : currentPortfolioValue,
          
          investmentTerm: {
            years: investmentTermYears,
            months: investmentTermMonths
          },
          
          startDate: startDateObj,
          endDate: history.length > 0 ? history[history.length - 1].date : null,
          
          assetAllocation: assetAllocationData,
          expectedAssetReturns: formData.expectedReturns,
          
          useIIS,
          iisType,
          iisDeductionLimit,
          
          benchmarkComparison: {
            conservativePortfolio: calculateBenchmarkPerformance(initialInvestment, monthlyContribution, investmentTermMonths, 0.09),
            balancedPortfolio: calculateBenchmarkPerformance(initialInvestment, monthlyContribution, investmentTermMonths, 0.115),
            aggressivePortfolio: calculateBenchmarkPerformance(initialInvestment, monthlyContribution, investmentTermMonths, 0.142)
          }
        };
        
        // Устанавливаем результаты и графики
        setResults(finalResults);
        setPortfolioHistory(history);
        setAssetAllocationChart(assetAllocationData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setPortfolioHistory([]);
        setAssetAllocationChart([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, assetTypes, validateForm, errors, setErrors, setResults, setPortfolioHistory, setAssetAllocationChart, setIsCalculating]);

  // Вспомогательная функция для расчета бенчмарка
  const calculateBenchmarkPerformance = (initialAmount, monthlyContribution, months, annualRate) => {
    let total = initialAmount;
    const monthlyRate = annualRate / 12;
    
    for (let i = 0; i < months; i++) {
      total = total * (1 + monthlyRate) + monthlyContribution;
    }
    
    return {
      finalValue: total,
      absoluteReturn: total - (initialAmount + monthlyContribution * months),
      percentageReturn: ((total / (initialAmount + monthlyContribution * months)) - 1) * 100
    };
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      // Базовые параметры
      initialInvestment: '100000',
      monthlyContribution: '10000',
      investmentTerm: '10',
      startDate: new Date().toISOString().split('T')[0],
      
      // Структура портфеля
      portfolioType: 'balanced',
      assetAllocation: {
        stocks: 50,
        bonds: 30,
        cash: 10,
        gold: 5,
        realEstate: 5
      },
      
      // Настройки доходности
      expectedReturns: {
        stocks: '15',
        bonds: '9',
        cash: '7',
        gold: '8',
        realEstate: '10'
      },
      
      // Настройки инфляции
      includeInflation: true,
      inflationRate: '7',
      
      // Настройки налогов
      includeTaxes: true,
      taxRate: '13',
      
      // Настройки комиссий
      includeFees: true,
      brokerFee: '0.3',
      managementFee: '1',
      
      // Настройки для ИИС
      useIIS: false,
      iisType: 'taxDeduction',
      iisDeductionLimit: '400000',
      
      // Пополнения/снятия
      includeCashFlow: false,
      additionalInvestments: [],
      withdrawals: []
    });
    
    setResults(null);
    setPortfolioHistory([]);
    setAssetAllocationChart([]);
    setErrors({});
    setSavePortfolioName('');
    setShowSaveDialog(false);
  };

  // Экспорт портфеля в CSV
  const exportToCSV = () => {
    if (!portfolioHistory || portfolioHistory.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Месяц', 'Дата', 'Начальный баланс', 'Ежемесячный взнос', 'Дополнительные инвестиции', 'Снятия', 'Комиссии'];
    
    if (formData.useIIS && formData.iisType === 'taxDeduction') headers.push('Налоговый вычет');
    if (formData.includeInflation) headers.push('Влияние инфляции');
    
    // Добавляем активы в заголовки
    assetTypes.forEach(type => {
      if (formData.assetAllocation[type.id] > 0) {
        headers.push(type.name);
      }
    });
    
    headers.push('Конечный баланс');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = portfolioHistory.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.startBalance),
        formatCsvNumber(item.contributions),
        formatCsvNumber(item.additionalInvestments),
        formatCsvNumber(item.withdrawals),
        formatCsvNumber(item.fees)
      ];
      
      if (formData.useIIS && formData.iisType === 'taxDeduction') rowData.push(formatCsvNumber(item.taxDeduction));
      if (formData.includeInflation) rowData.push(formatCsvNumber(item.inflation));
      
      // Добавляем значения активов
      assetTypes.forEach(type => {
        if (formData.assetAllocation[type.id] > 0) {
          rowData.push(formatCsvNumber(item.assetValues[type.id]));
        }
      });
      
      rowData.push(formatCsvNumber(item.endBalance));
      
      return rowData.join(';');
    });

    // Добавим итоговую строку
          const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.initialInvestment),
      formatCsvNumber(results.totalContributions),
      '', // Дополнительные инвестиции
      formatCsvNumber(results.totalWithdrawals),
      formatCsvNumber(results.totalFeesPaid)
    ];
    
    if (formData.useIIS && formData.iisType === 'taxDeduction') totalRow.push(formatCsvNumber(results.totalTaxDeductions));
    if (formData.includeInflation) totalRow.push(formatCsvNumber(results.totalInflationImpact));
    
    // Добавляем пустые места для активов
    assetTypes.forEach(type => {
      if (formData.assetAllocation[type.id] > 0) {
        totalRow.push('');
      }
    });
    
    totalRow.push(formatCsvNumber(results.finalPortfolioValue));
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Начальная сумма инвестиций:', formatCsvNumber(results.initialInvestment)],
      ['Срок инвестирования:', `${results.investmentTerm.years} лет (${results.investmentTerm.months} мес.)`],
      ['Ежемесячный взнос:', formatCsvNumber(parseFloatOrDefault(formData.monthlyContribution))],
      ['Конечная стоимость портфеля:', formatCsvNumber(results.finalPortfolioValue)],
      ['Общая доходность:', `${formatCsvNumber(results.percentageReturn)}%`],
      ['Среднегодовая доходность:', `${formatCsvNumber(results.annualizedReturn)}%`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'инвестиционный_портфель.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения портфеля
  const handleSavePortfolio = () => {
    if (showSaveDialog) {
      const savedResult = savePortfolio(savePortfolioName || null);
      if (savedResult) {
        alert(`Портфель "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSavePortfolioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateInvestmentReturns();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <LineChart className="w-6 h-6 text-indigo-500" />
          Параметры инвестиций
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Базовые параметры инвестиций */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Структура портфеля */}
        <PortfolioStructureSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleAssetAllocationChange={handleAssetAllocationChange}
          errors={errors} 
          portfolioTypes={portfolioTypes}
          assetTypes={assetTypes}
        />

        {/* Налоги и комиссии */}
        <div className="mb-6 space-y-4">
          {/* Блок налогов */}
          <TaxesAndFeesSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />

          {/* Блок инфляции */}
          <InflationSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />

          {/* Блок ИИС */}
          <IISSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />

          {/* Блок денежных потоков */}
          <CashFlowSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        </div>

        {/* Диалог сохранения портфеля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={savePortfolioName}
                onChange={(e) => setSavePortfolioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSavePortfolio}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать доходность'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {portfolioHistory.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать данные (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSavePortfolio}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;